import { useMemo, type ReactNode } from "react";
import { useMarketingResources } from "creso-core/hooks";
import { V2Loader, cn } from "web-design-system";
import { Page } from "@core/components/layout/page";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { MarketingResourceType } from "creso-core/types";
import { getFeaturedWebinar, isUpcomingWebinar } from "creso-core/utils";
import { ArticleCard } from "../components/article-card";
import { NewsletterRow } from "../components/newsletter-featured-row";
import { MarketingFeaturedCard } from "../components/marketing-featured-card";
import { WebinarFeaturedCard } from "../components/webinar-featured-card";
import VideoCard from "../components/video-card";

type SectionHeaderProps = {
  title: string;
  viewAllTo?: string;
  viewAllCategory?: Exclude<MarketingResourceType, "webinar">;
  className?: string;
};

export function SectionHeader({
  title,
  viewAllTo,
  viewAllCategory,
  className,
}: SectionHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between gap-3", className)}>
      <div className="text-lg font-semibold tracking-tight">{title}</div>
      {viewAllTo ? (
        <Link
          to={viewAllTo}
          search={{ category: viewAllCategory }}
          className="flex items-center gap-0.5 text-sm text-foreground shrink-0"
        >
          View All
          <ChevronRight className="size-4" />
        </Link>
      ) : null}
    </div>
  );
}

function SectionCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-border/50 bg-card p-4",
        className
      )}
    >
      {children}
    </section>
  );
}

export default function FeaturedResourcesPage() {
  const { data: marketingResources = [], isLoading: isMarketingLoading } =
    useMarketingResources({
      resource_type: "marketing_poster",
    });

  const { data: webStoryResources = [], isLoading: isWebStoryLoading } =
    useMarketingResources({
      resource_type: "web_story",
    });

  const featuredMarketing = useMemo(() => {
    const resources = [];
    if (marketingResources.length > 0) {
      resources.push(...marketingResources.slice(0, 1));
    }
    if (webStoryResources.length > 0) {
      resources.push(...webStoryResources.slice(0, 1));
    }
    return resources;
  }, [marketingResources, webStoryResources]);

  const { data: webinarResources = [], isLoading: isWebinarLoading } =
    useMarketingResources({
      resource_type: "webinar",
    });

  const { data: newsletterResources = [], isLoading: isNewslettersLoading } =
    useMarketingResources({
      resource_type: "newsletter",
    });

  const { data: articleResources = [], isLoading: isArticlesLoading } =
    useMarketingResources({
      resource_type: "article",
    });

  const { data: tutorialResources = [], isLoading: isTutorialsLoading } =
    useMarketingResources({
      resource_type: "tutorial",
    });

  const featuredWebinar = useMemo(() => {
    const resources = webinarResources.length > 0 ? webinarResources : [];
    const webinar = getFeaturedWebinar(resources);

    return webinar ? [webinar] : [];
  }, [webinarResources]);

  const featuredNewsletters = useMemo(() => {
    return newsletterResources.length > 0
      ? newsletterResources.slice(0, 3)
      : [];
  }, [newsletterResources]);

  const featuredArticles = useMemo(() => {
    return articleResources.length > 0 ? articleResources.slice(0, 2) : [];
  }, [articleResources]);

  const featuredTutorials = useMemo(() => {
    return tutorialResources.length > 0 ? tutorialResources.slice(0, 4) : [];
  }, [tutorialResources]);

  const isLoading =
    isMarketingLoading ||
    isWebStoryLoading ||
    isWebinarLoading ||
    isNewslettersLoading ||
    isArticlesLoading ||
    isTutorialsLoading;

  return (
    <>
      <Page.Header className="top-12" title="Featured Resources" />
      <Page.Body>
        <V2Loader isLoading={isLoading} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 xl:grid-cols-7 gap-4">
            <SectionCard className="col-span-1 xl:col-span-4">
              <SectionHeader title="Marketing" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
                {featuredMarketing?.slice(0, 2).map((item) => (
                  <MarketingFeaturedCard key={item.id} item={item} />
                ))}
              </div>
            </SectionCard>

            <SectionCard className="col-span-1 xl:col-span-3">
              <SectionHeader title="Webinar" viewAllTo="/resources/webinars" />
              {featuredWebinar.map((item) => (
                <WebinarFeaturedCard
                  key={item.id}
                  item={item}
                  isUpcoming={isUpcomingWebinar(item)}
                  className="h-120 xl:h-76"
                />
              ))}
            </SectionCard>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-7 gap-4">
            <SectionCard className="col-span-1 xl:col-span-7 h-full">
              <SectionHeader title="Knowledge" />

              <div className="grid grid-cols-1 xl:grid-cols-7 gap-4 h-full">
                <SectionCard className="col-span-1 xl:col-span-3 bg-background">
                  <SectionHeader
                    title="Newsletters"
                    viewAllTo="/resources/knowledge"
                    viewAllCategory="newsletter"
                  />
                  <div className="flex flex-col h-full">
                    {featuredNewsletters.map((item, index) => (
                      <NewsletterRow
                        key={item.id}
                        item={item}
                        showBorder={index < featuredNewsletters.length - 1}
                      />
                    ))}
                  </div>
                </SectionCard>

                <SectionCard className="col-span-1 xl:col-span-4 bg-background">
                  <SectionHeader
                    title="Articles"
                    viewAllTo="/resources/knowledge"
                    viewAllCategory="article"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
                    {featuredArticles.map((item) => (
                      <ArticleCard key={item.id} item={item} />
                    ))}
                  </div>
                </SectionCard>
              </div>

              <SectionCard className="bg-background">
                <SectionHeader
                  title="Platform Tutorials"
                  viewAllTo="/resources/knowledge"
                  viewAllCategory="tutorial"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {featuredTutorials.map((item) => (
                    <VideoCard
                      key={item.id}
                      item={item}
                      content={
                        <div className="truncate px-3 py-2 text-sm font-medium">
                          {item.title}
                        </div>
                      }
                    />
                  ))}
                </div>
              </SectionCard>
            </SectionCard>
          </div>
        </V2Loader>
      </Page.Body>
    </>
  );
}
